import React from "react";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";
import classes from "./DurationFieldInput.scss";

// Utility function to check if keypress is a number (reused from UIInputNumberField)
const isNumber = evt => {
  const charCode = evt.charCode;
  if (charCode >= 48 && charCode <= 57) {
    return true;
  }
  return false;
};

class DurationFieldInput extends UIBaseComponent {
  state = {
    isFocused: false,
  };

  // Regex to match only positive integers
  numericRegex = /^[0-9]*$/;

  handleInputFocus = () => {
    this.setState({ isFocused: true });
  };

  handleKeyPress = e => {
    // Reject non-numeric keys
    if (!isNumber(e)) {
      e.preventDefault();
      return false;
    }

    // Block input if already at 3 digits
    const val = this.getValue()?.toString() || "";
    if (val.length >= 3) {
      e.preventDefault();
      return false;
    }

    return true;
  };

  handlePaste = e => {
    e.preventDefault();
    return false;
  };

  handleInputChange = event => {
    let value = event.target.value;
    const { min, max } = this.props;

    // Check if the input matches the numeric regex
    if (value && !this.numericRegex.test(value)) {
      return;
    }

    // Remove leading zeros but allow a single "0"
    value = value.replace(/^0+(?=\d)/, "");

    if (value) {
      const numericValue = parseInt(value, 10);
      if (numericValue > max) {
        value = max;
      } else if (numericValue < min) {
        value = min;
      }
    }

    this.updateValue(value);
  };

  handleInputBlur = () => {
    const { value, min } = this.props;
    if (!value || value === "" || !this.numericRegex.test(value.toString())) {
      this.updateValue(min);
    }
    this.setState({ isFocused: false });
  };

  getValue = () => {
    const { value } = this.props;
    return value === undefined || value === null ? "" : value.toString();
  };

  renderNumberInput = () => {
    const { disabled = false, testId, min, max, step, t } = this.props;
    const { isFocused } = this.state;
    const valStr = this.getValue();
    const numeric = parseInt(valStr, 10) || min;

    // Show placeholder when value is zero and not focused
    const isPlaceholder = !isFocused && numeric === min;
    const displayValue = isPlaceholder ? "" : valStr;

    const inputClassName = classNames(classes.numberInput, "text-body");

    const unitClass = classNames(
      classes.unit,
      { [classes.unitSingle]: (displayValue || "").length === 1 },
      { [classes.unitTriple]: (displayValue || "").length === 3 },
      "text-body"
    );

    return (
      <div className={classNames(classes.container, "text-body")}>
        <input
          type="number"
          className={inputClassName}
          value={displayValue}
          placeholder={t("common:not_selected")}
          onFocus={this.handleInputFocus}
          onChange={this.handleInputChange}
          onBlur={this.handleInputBlur}
          disabled={disabled}
          data-testid={testId}
          min={min}
          max={max}
          step={step}
          onKeyPress={this.handleKeyPress}
          onPaste={this.handlePaste}
          ref={ref => {
            this.inputRef = ref;
          }}
        />
        {!isPlaceholder && (
          <span className={unitClass}>
            {numeric === 1 ? t("common:hour") : t("common:hours")}
          </span>
        )}
      </div>
    );
  };

  renderViewValue = () => {
    const { viewContainerStyle, min, t } = this.props;
    const { isFocused } = this.state;

    const numeric = parseInt(this.getValue(), 10) || min;
    const isPlaceholder = !isFocused && numeric === min;

    return isPlaceholder ? (
      <div
        style={viewContainerStyle}
        className={classNames(classes.placeholder, "text-body")}
      >
        {t("common:not_selected")}
      </div>
    ) : (
      <div
        style={viewContainerStyle}
        className={classNames(classes.viewContainer, "text-body")}
      >
        {t("common:hour_count", { count: numeric })}
      </div>
    );
  };

  renderEdit = () => this.renderNumberInput();
  renderView = () => this.renderViewValue();
}

const mapStateToProps = (state, ownProps) => {
  const { mode, disabled = false } = ownProps;

  return {
    disabled: disabled || mode === "view",
  };
};

DurationFieldInput.defaultProps = {
  ...UIBaseComponent.defaultProps,
  min: 0,
  max: 999,
  step: 1,
  overlayStyle: {},
  viewContainerStyle: {},
  testId: "UIComponents-DurationfieldInput",
};

DurationFieldInput.propTypes = {
  ...UIBaseComponent.propTypes,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  overlayStyle: PropTypes.object,
  viewContainerStyle: PropTypes.object,
  testId: PropTypes.string,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, null)
)(DurationFieldInput);
